import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import '../styles/NoteHighlighting.css';
import { getEnharmonicNoteName, getProperNoteDisplay, resolveAllNotesIndex } from '../utils/musicTheory';
import { calculateOptimalFretboardLayout, getGuitarTuning, getNoteAtFret, generateStringNotes, getFretInlays, DOUBLE_DOT_FRETS } from './GuitarNeckVisualizer.utils';
import useTouchHandler from '../hooks/useTouchHandler';
import { isArpeggioNote, getArpeggioDegree, getFretNoteName, getFretMidi } from '../utils/guitarNeckUtils';
import { playFretNote, warmUpPiano } from '../utils/notePiano';
import { isLightDevice } from '../utils/devicePerformance';
import { noLessonAnimations } from '../utils/perfVariant';
import { useUserSetting } from '../data/hooks/useUserSetting';
import { 
  useScaleLogic,
  useStringVisibility,
  useNoteSelection,
  usePatternGeneration,
  usePresetManagement,
  useArpeggioHighlighting,
  useChordHighlighting,
  useConnectionLines,
  useGuitarTuning,
  generateNotePattern
} from './GuitarNeckVisualizer.hooks';
import FretboardHeader from './FretboardHeader';
import StringRow, { getFretCellState, hasFretNoteMarker, FretNoteMarker } from './StringRow';
// HighlightControl removed
import PresetDialogs from './PresetDialogs';
import NeckControls from './NeckControls';
import LayerVisibilityToggler from './LayerVisibilityToggler';
import FretboardLockOverlay from './FretboardLockOverlay';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import Tip from './Tip';
import { NOTE_COLORS } from '../styles/NoteStyles';
import { collectFretboardState } from '../utils/fretboardStateCollector';
import { isDesktopBrowser } from '../utils/platformUtils';
import { TOGGLE_NECK_VIEW_EVENT } from '../utils/desktopShortcuts';
import logService from '../utils/logService';

const SHOW_NUT = false;

// Extracted outside GuitarNeckVisualizer to avoid hook re-creation on every render (P0 fix)
// Le nom de corde est la case frette 0 : la corde a vide EST la note qu'il
// nomme. Il rend le meme marqueur que les autres cases (en compact) quand la
// note a vide est dans la gamme, ou dans la section pendant une lecon, et la
// lettre seule sinon. Tap = jouer la note a vide, appui long = masquer la corde.
// Le marqueur n'a pas de handleNoteClick : la cellule porte les deux gestes, le
// marqueur ne fait que son flash de pression.
export const StringLabelCell = React.memo(({ stringName, stringIndex, onTap, onLongPress, cell, lessonNote, noteOpacity, isEarLit, markerProps, leftHanded = false }) => {
  const { touchHandlers } = useTouchHandler(onTap, onLongPress);
  const showMarker = !!cell && hasFretNoteMarker(cell, markerProps.lessonMode, lessonNote);

  return (
    <Box
      className={`fretboard-string-name string-${stringIndex}`}
      sx={{
        height: 'var(--string-row-height, 36px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        paddingLeft: leftHanded ? '5px' : '14px',
        paddingRight: leftHanded ? '14px' : '5px',
        paddingTop: '14px',
        '@media (orientation: landscape)': {
          paddingLeft: '2px',
          paddingRight: '2px',
        },
        cursor: 'pointer',
        userSelect: 'none',
      }}
      {...touchHandlers}
    >
      {showMarker ? (
        <FretNoteMarker
          cell={cell}
          lessonNote={lessonNote}
          compact
          noteOpacity={noteOpacity}
          isEarLit={isEarLit}
          stringIndex={stringIndex}
          {...markerProps}
        />
      ) : (
        <Typography
          className="fretboard-string-name-text"
          variant="body2"
          sx={{
            fontWeight: 'bold',
            fontSize: '0.8rem',
            lineHeight: 1,
          }}
        >
          {stringName}
        </Typography>
      )}
    </Box>
  );
});

// Tableau vide partage. Une corde sans note de lecon recoit toujours cette
// meme reference, jamais null, sinon StringRow sort du mode lecon et repeint
// toute la gamme.
const EMPTY = [];
const NO_INLAYS = [];

// Egalite note a note de deux tranches, dans l'ordre : c'est ce qui permet de
// rendre l'ancienne tranche quand un pas n'a rien change sur cette corde.
const sameLessonSlice = (a, b) => {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const left = a[i];
    const right = b[i];
    if (left === right) continue;
    if (!left || !right) return false;
    if (left.stringIndex !== right.stringIndex || left.fret !== right.fret
      || left.current !== right.current || left.label !== right.label) return false;
  }
  return true;
};

const GuitarNeckVisualizer = ({ rootNote, currentMode, activeChordNote, playingNote, chordExtension = '3', className = "", selectedNotes = [], onSelectedNotesChange, onShowPatternSnackbar, currentProgression, isProgressionPlaying = false, isPaintMode = false, onPaintModeChange, isCompactMode = false, coachStep = null, discoveryTip = null, onDiscoveryTipDismiss = null, lessonNotes = null, scrollToFret = null }) => {
  
  const guitarTuning = useGuitarTuning();
  const [leftHanded] = useUserSetting('leftHanded', false);
  // Lab : repere de frette dans les ronds vides. Lu une fois ici, jamais dans
  // la cellule, il y en a plus de cent par manche.
  const [fretMarkerDots] = useUserSetting('fretMarkerDots', true);
  // Les incrustations entre les cordes. Allumees par defaut, c'est le manche
  // tel qu'il a toujours ete ; on les eteint quand le repere passe dans les ronds.
  const [neckInlays] = useUserSetting('neckInlays', false);
  const handednessRef = useRef(false);
  const scrollTimerRef = useRef(null);
  const scrollGeometryRef = useRef({ max: 0, distance: 0, visible: false });
  const theme = useTheme();
  const isLandscape = useMediaQuery('(orientation: landscape)');
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // References for scrollable elements
  const fretboardRef = useRef(null);
  const topHeaderRef = useRef(null);
  const fretsContainerRef = useRef(null);
  const bottomHeaderRef = useRef(null);
  const [availableWidth, setAvailableWidth] = useState(0);

  // Preload the tap piano so the first tapped note isn't silent
  useEffect(() => { warmUpPiano(); }, []);

  // Index into the Ear preview sequence currently sounding, or null when idle.
  // The sequence is the displayed scale shape (3nps), so the glow walks the exact
  // notes shown on the fretboard, one position at a time.
  const [earStepIndex, setEarStepIndex] = useState(null);

  // Update available width when component mounts or window resizes
  useEffect(() => {
    const updateWidth = () => {
      if (fretboardRef.current) {
        setAvailableWidth(fretboardRef.current.offsetWidth);
      }
    };
    
    // Initial measurement
    updateWidth();
    
    // Add resize listener
    window.addEventListener('resize', updateWidth);
    
    // Cleanup
    return () => window.removeEventListener('resize', updateWidth);
  }, []); // No dependencies needed
  
  // Reset manual scroll flag when scale context changes
  useEffect(() => {
    hasUserScrolledRef.current = false;
  }, [rootNote, currentMode]);

  // Detect manual scroll on fretboard container (passive, no re-render)
  useEffect(() => {
    const el = fretsContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      const max = Math.max(0, el.scrollWidth - el.clientWidth);
      if (!el.clientWidth || !scrollGeometryRef.current.visible || max !== scrollGeometryRef.current.max) return;
      scrollGeometryRef.current.distance = handednessRef.current ? max - el.scrollLeft : el.scrollLeft;
      if (!isAutoScrollingRef.current) hasUserScrolledRef.current = true;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // We no longer need scroll synchronization since we have a single scrollable container
  
  // Nous avons supprimé l'effet et la fonction liés au mode plein écran
  
  // Chord degree the green anchor follows (voice leading drills). 1 = the chord
  // root, i.e. the neck as it was before this control existed.
  const [trackedDegree, setTrackedDegree] = useState(1);

  // The 7th only sounds in a 7th-chord voicing: dropping back to a triad must
  // not leave the anchor chasing a degree that is no longer being played.
  useEffect(() => {
    if (chordExtension !== '7') {
      setTrackedDegree(prev => (prev === 7 ? 5 : prev));
    }
  }, [chordExtension]);

  // Hook for managing scale and mode logic
  const { activeScale } = useScaleLogic(rootNote, currentMode, activeChordNote, chordExtension, trackedDegree);
  
  // Hook for managing string visibility
  const { visibleStrings, toggleStringVisibility, setVisibleStrings } = useStringVisibility();
  
  // State for tracking if any notes are selected
  const [hasSelectedNote, setHasSelectedNote] = useState(selectedNotes.length > 0);
  
  // State for tracking if we're in start note selection mode
  const [isStartNoteSelectionMode, setIsStartNoteSelectionMode] = useState(false);
  
  // iOS Safari fix: track last clicked note to prevent rapid-fire events
  const lastClickedRef = useRef({ stringIndex: -1, fret: -1, timestamp: 0 });

  // Track manual scroll to preserve user's chosen position during progression play
  const hasUserScrolledRef = useRef(false);
  const isAutoScrollingRef = useRef(false);
  const hasScrolledForPlayRef = useRef(false);
  // Derniere frette demandee par le lecteur de lecon : la meme valeur ne
  // reprovoque pas de defilement (pas suivant sur la meme frette).
  const lastScrollToFretRef = useRef(null);
  const scrollToFretRef = useRef(scrollToFret);
  scrollToFretRef.current = scrollToFret;

  // State for fretboard lock - read from the user mirror, synced.
  const [isFretboardLocked, setIsFretboardLocked] = useUserSetting('fretboardLocked', false);

  const moveScroll = useCallback((element, left, behavior = 'smooth') => {
    isAutoScrollingRef.current = true;
    clearTimeout(scrollTimerRef.current);
    const max = Math.max(0, element.scrollWidth - element.clientWidth);
    const position = Math.min(max, Math.max(0, left));
    scrollGeometryRef.current = { max, distance: handednessRef.current ? max - position : position, visible: true };
    element.scrollTo({ left, behavior });
    scrollTimerRef.current = setTimeout(() => { isAutoScrollingRef.current = false; }, 500);
  }, []);

  const scrollFromNut = useCallback((position) => {
    const element = fretsContainerRef.current;
    if (!element) return;
    if (!element.clientWidth) {
      scrollGeometryRef.current.distance = position;
      return;
    }
    const left = handednessRef.current
      ? Math.max(0, element.scrollWidth - element.clientWidth - position)
      : position;
    moveScroll(element, left);
  }, [moveScroll]);

  useLayoutEffect(() => {
    const element = fretsContainerRef.current;
    if (!element) return undefined;
    const max = Math.max(0, element.scrollWidth - element.clientWidth);
    const changed = handednessRef.current !== leftHanded;
    handednessRef.current = leftHanded;
    if (!element.clientWidth) {
      scrollGeometryRef.current.visible = false;
    } else if (changed) {
      moveScroll(element, Math.max(0, max - element.scrollLeft), 'instant');
    } else {
      scrollGeometryRef.current = { max, distance: leftHanded ? max - element.scrollLeft : element.scrollLeft, visible: true };
    }
    const observer = new ResizeObserver(() => {
      if (!element.clientWidth) {
        scrollGeometryRef.current.visible = false;
        return;
      }
      const nextMax = Math.max(0, element.scrollWidth - element.clientWidth);
      const { distance, visible } = scrollGeometryRef.current;
      if (visible && nextMax === scrollGeometryRef.current.max) return;
      if (scrollToFretRef.current != null && !isFretboardLocked) {
        const targetDistance = Math.max(0, (scrollToFretRef.current - 1.5) * 60);
        moveScroll(element, Math.max(0, leftHanded ? nextMax - targetDistance : targetDistance), 'instant');
      } else if (leftHanded || !visible) {
        moveScroll(element, Math.max(0, leftHanded ? nextMax - distance : distance), 'instant');
      } else {
        scrollGeometryRef.current = { max: nextMax, distance: element.scrollLeft, visible: true };
      }
    });
    observer.observe(element);
    if (element.firstElementChild) observer.observe(element.firstElementChild);
    return () => observer.disconnect();
  }, [isFretboardLocked, leftHanded, moveScroll]);

  useEffect(() => () => clearTimeout(scrollTimerRef.current), []);

  // Function to toggle fretboard lock
  const handleToggleFretboardLock = useCallback(() => {
    setIsFretboardLocked(!isFretboardLocked);
  }, [isFretboardLocked, setIsFretboardLocked]);

  // Add keyboard shortcut for lock toggle
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Check if 'L' key is pressed (case-insensitive)
      if (event.key.toLowerCase() === 'l' && !event.ctrlKey && !event.altKey && !event.metaKey) {
        // Make sure we're not typing in an input field
        if (event.target.tagName !== 'INPUT' && event.target.tagName !== 'TEXTAREA') {
          event.preventDefault();
          handleToggleFretboardLock();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleToggleFretboardLock]);

  // State for highlighted frets (multiple selection)
  const [highlightedFrets, setHighlightedFrets] = useState([]);

  // State for current color selection - using the color IDs from memory
  const [currentColor, setCurrentColor] = useState('orange'); // Default to orange
  
  // State for visible colors - all colors are visible by default
  const [visibleColors, setVisibleColors] = useState(['orange', 'red', 'green', 'blue']);
  
  // Function to handle color selection
  const handleColorSelect = (color) => {
    setCurrentColor(color);
  };
  
  // Function to toggle color visibility
  const handleToggleColorVisibility = (colorId) => {
    setVisibleColors(prev => {
      if (prev.includes(colorId)) {
        return prev.filter(c => c !== colorId);
      } else {
        return [...prev, colorId];
      }
    });
  };
  
  // Function to handle fret click in the header
  const handleFretClick = useCallback((fretNumber) => {
    // Toggle the highlighted fret - add or remove from the array
    setHighlightedFrets(prevFrets => {
      if (prevFrets.includes(fretNumber)) {
        // Remove the fret if it's already highlighted
        return prevFrets.filter(fret => fret !== fretNumber);
      } else {
        // Add the fret if it's not highlighted
        return [...prevFrets, fretNumber];
      }
    });
  }, []);

  // Tap sur le nom de corde : jouer la corde a vide, meme chemin audio que les
  // autres cases. Passe a cote de handleNoteClick expres : ni peinture de la
  // frette 0 en paint mode, ni selection de note de depart.
  const playOpenString = useCallback((stringIndex) => {
    playFretNote(getFretNoteName(stringIndex, 0));
  }, []);

  // Function to handle start note selection (long press)
  const handleStartNoteSelection = useCallback((stringIndex, fret, noteName, resetFirst = false) => {
    if (!onSelectedNotesChange || !activeScale) {
      return;
    }

    const timestampBase = Date.now();
    const { pattern: anchoredPattern = [] } = generateNotePattern(activeScale, {
      anchorToStartNote: true,
      startStringIndex: stringIndex,
      startFret: fret
    }) || {};

    if (resetFirst) {
      setHighlightedFrets([]);
    }

    const filteredExisting = resetFirst ? [] : selectedNotes.filter(note => {
      if (note.stringIndex < stringIndex) return true;
      const fretsForString = anchoredPattern[note.stringIndex];
      return !(Array.isArray(fretsForString) && fretsForString.includes(note.fret));
    });

    const updatedNotes = [...filteredExisting];

    anchoredPattern.forEach((frets, sIdx) => {
      if (!Array.isArray(frets) || frets.length === 0 || sIdx < stringIndex) {
        return;
      }

      frets.forEach((anchoredFret, orderIdx) => {
        const alreadyExists = updatedNotes.some(n => n.stringIndex === sIdx && n.fret === anchoredFret);
        if (alreadyExists) {
          return;
        }

        const anchoredNoteValue = getNoteAtFret(getGuitarTuning()[sIdx], anchoredFret);
        updatedNotes.push({
          stringIndex: sIdx,
          fret: anchoredFret,
          note: getProperNoteDisplay(anchoredNoteValue, activeScale),
          color: currentColor,
          timestamp: timestampBase + (sIdx - stringIndex) * 10 + orderIdx
        });
      });
    });

    const originalExists = updatedNotes.some(n => n.stringIndex === stringIndex && n.fret === fret);
    if (!originalExists) {
      updatedNotes.unshift({
        stringIndex,
        fret,
        note: getProperNoteDisplay(noteName, activeScale),
        color: currentColor,
        timestamp: timestampBase - 1000
      });
    }

    onSelectedNotesChange(updatedNotes);
    setHasSelectedNote(updatedNotes.length > 0);

    if (onShowPatternSnackbar) {
      onShowPatternSnackbar(false);
    }

    setIsStartNoteSelectionMode(false);
  }, [onSelectedNotesChange, activeScale, selectedNotes, currentColor, onShowPatternSnackbar]);

  // Custom note click handler with same-note debouncing
  const handleNoteClick = useCallback((stringIndex, fret, noteName) => {
    const now = Date.now();
    const lastClick = lastClickedRef.current;
    
    // Debounce same note clicks (iOS Safari rapid-fire protection)
    if (lastClick.stringIndex === stringIndex &&
        lastClick.fret === fret &&
        (now - lastClick.timestamp) < 300) {
      return;
    }
    
    lastClickedRef.current = { stringIndex, fret, timestamp: now };
    
    if (isStartNoteSelectionMode) {
      handleStartNoteSelection(stringIndex, fret, noteName);
      return;
    }

    // Default behaviour (paint mode off): a tap simply plays the note so the user
    // hears the pitch they touched. Paint mode is opt-in via the toolbar toggle.
    if (!isPaintMode) {
      playFretNote(getFretNoteName(stringIndex, fret));
      return;
    }

    const existingNote = selectedNotes.find(n => n.stringIndex === stringIndex && n.fret === fret);
    let newSelectedNotes;

    if (existingNote) {
      // Remove the note
      newSelectedNotes = selectedNotes.filter(n => !(n.stringIndex === stringIndex && n.fret === fret));
      setHasSelectedNote(newSelectedNotes.length > 0);
    } else {
      // Add the note
      newSelectedNotes = [...selectedNotes, {
        stringIndex,
        fret,
        note: noteName,
        color: currentColor
      }];
      setHasSelectedNote(true);
    }

    if (onSelectedNotesChange) {
      onSelectedNotesChange(newSelectedNotes);
    }
  }, [isStartNoteSelectionMode, handleStartNoteSelection, isPaintMode, selectedNotes, currentColor, onSelectedNotesChange]);

  // New note interaction handler for tap/long press
  const handleNoteInteraction = useCallback((stringIndex, fret, noteName, interactionType) => {
    if (interactionType === 'tap') {
      // Do nothing on tap - tap functionality removed
    } else if (interactionType === 'longPress') {
      // Long press: reset existing notes then generate scale pattern
      handleStartNoteSelection(stringIndex, fret, noteName, true);
    }
  }, [handleStartNoteSelection]);
  
  // Function to reset all highlighted frets
  const handleResetFretSelection = () => {
    setHighlightedFrets([]);
  };

  // Remise a plat du manche : toutes les cordes revisibles, plus aucune frette
  // surlignee. Toute nouvelle facon de masquer ou de mettre en avant quelque
  // chose sur le manche entrera ici, et nulle part ailleurs.
  const canResetNeck = visibleStrings !== null || highlightedFrets.length > 0;
  const handleResetNeckView = useCallback(() => {
    setVisibleStrings(null);
    setHighlightedFrets([]);
  }, [setVisibleStrings]);

  // Hook for pattern generation
  const { pattern, isNoteInPattern, rootFretOnLowE, lowestNoteOnAnyString } = usePatternGeneration(activeScale);

  // Ear preview sequence: one octave of the displayed scale shape (3nps), from the
  // root up to the next root, then back down. For each step we pick the position
  // closest to the previous one (greedy), so the run stays in one box on the neck
  // instead of jumping to a far unison of the same pitch.
  const earSequence = useMemo(() => {
    if (!Array.isArray(pattern)) return [];
    const positions = [];
    guitarTuning.forEach((open, s) => {
      (pattern[s] || []).forEach((fret) => {
        const midi = getFretMidi(s, fret);
        if (midi != null) positions.push({ stringIndex: s, fret, midi, note: getFretNoteName(s, fret) });
      });
    });
    if (positions.length === 0) return [];

    // Group the available positions by pitch
    const byMidi = new Map();
    positions.forEach((p) => {
      if (!byMidi.has(p.midi)) byMidi.set(p.midi, []);
      byMidi.get(p.midi).push(p);
    });

    // Anchor on the lowest-PITCHED root in the shape (lowest fret as tiebreaker),
    // so the run starts from the bass root, not a same-name root higher up the neck.
    const rootPc = resolveAllNotesIndex(rootNote);
    const rootPositions = positions.filter((p) => p.midi % 12 === rootPc);
    if (rootPositions.length === 0) return [];
    let current = rootPositions.reduce((a, b) =>
      (a.midi < b.midi || (a.midi === b.midi && a.fret <= b.fret)) ? a : b
    );
    const rootMidi = current.midi;

    // Distinct pitches from the root up one octave
    const midisAsc = [...byMidi.keys()].filter((m) => m >= rootMidi && m <= rootMidi + 12).sort((a, b) => a - b);
    // Closest FRET to the last played fret first (string distance only breaks ties):
    // the merged root+octave boxes duplicate pitches far up the neck, and weighting
    // strings equally could jump the run out of the current box.
    const cost = (a, b) => Math.abs(a.fret - b.fret) * 100 + Math.abs(a.stringIndex - b.stringIndex);

    const up = [current];
    for (let k = 1; k < midisAsc.length; k++) {
      const cands = byMidi.get(midisAsc[k]);
      let pick = cands[0];
      for (const c of cands) if (cost(c, current) < cost(pick, current)) pick = c;
      up.push(pick);
      current = pick;
    }
    const down = up.slice(0, -1).reverse(); // descend without repeating the top note
    return [...up, ...down];
  }, [pattern, rootNote, guitarTuning]);

  const earPosition = earStepIndex != null ? (earSequence[earStepIndex] || null) : null;

  // State for hiding pattern notes
  const [hidePatternNotes, setHidePatternNotes] = useState(false);
  
  // State for highlighting only in-pattern triads
  const [highlightPatternTriadsOnly, setHighlightPatternTriadsOnly] = useState(false);

  // State for showing whole scale
  const [showWholeScale, setShowWholeScale] = useState(true);

  // State for note display mode: 'none' (no labels), 'degrees' (1,2,3...), or 'names' (C,D,E...)
  const [noteDisplayMode, setNoteDisplayMode] = useState('none');


  // Hook for arpeggio highlighting
  const { 
    highlightArpeggios, 
    setHighlightArpeggios, 
    selectedArpeggio,
    setSelectedArpeggio,
    isArpeggioNote, 
    getArpeggioDegree 
  } = useArpeggioHighlighting(activeScale);
  
  // Hook for chord highlighting
  const {
    highlightChordNotes,
    setHighlightChordNotes,
    chordNotesToHighlight,
    isChordNote,
    getChordDegree
  } = useChordHighlighting(activeChordNote, playingNote, activeScale);
  
  // Hook for connection lines visibility
  const { showConnectionLines, setShowConnectionLines } = useConnectionLines();
  
  // Hook for preset management
  const { 
    presetDialogOpen,
    setPresetDialogOpen,
    presetName,
    setPresetName,
    presets,
    loadPresetDialogOpen,
    setLoadPresetDialogOpen,
    savePreset,
    loadPreset,
    deletePreset
  } = usePresetManagement(
    rootNote, 
    currentMode, 
    selectedNotes, 
    visibleStrings, 
    hasSelectedNote, 
    activeScale
  );
  
  // Update hasSelectedNote when selectedNotes prop changes
  useEffect(() => {
    setHasSelectedNote(selectedNotes.length > 0);
  }, [selectedNotes]);
  
  // Calculate optimal fretboard layout
  const { startFret, numFretsToShow } = useMemo(() => calculateOptimalFretboardLayout(
    activeScale,
    availableWidth,
    isLandscape, // Just use landscape orientation
    isLargeScreen,
    false, // isMaximized removed
    isMobile
  ), [activeScale, availableWidth, isLandscape, isLargeScreen, isMobile]);

  // Stable identity for the pattern predicate passed to the 6 StringRow: an
  // inline ternary would hand them a fresh function on every render and defeat
  // their React.memo.
  const effectiveIsNoteInPattern = useMemo(() => {
    if (hidePatternNotes || showWholeScale) return () => false;
    return isNoteInPattern;
  }, [hidePatternNotes, showWholeScale, isNoteInPattern]);

  // Case frette 0 de chaque corde, rendue par le nom de corde : memes etats que
  // les cases de StringRow, calcules par la meme fonction.
  const openStringCells = useMemo(() => guitarTuning.map((stringName, stringIndex) => getFretCellState({
    stringName,
    stringIndex,
    fret: 0,
    activeScale,
    isNoteInPattern: effectiveIsNoteInPattern,
    isArpeggioNote,
    selectedArpeggio,
    isChordNote
  })), [guitarTuning, activeScale, effectiveIsNoteInPattern, isArpeggioNote, selectedArpeggio, isChordNote]);

  const lessonMode = Array.isArray(lessonNotes);

  // Raccourci S du navigateur de bureau : Safe / All vit ici, App n'y a pas
  // acces et passe par un evenement window. Pendant une lecon le segmente est
  // grise, la touche ne doit rien faire non plus.
  useEffect(() => {
    if (!isDesktopBrowser()) return;
    const onToggleView = () => {
      if (lessonMode) return;
      setShowWholeScale(previous => !previous);
    };
    window.addEventListener(TOGGLE_NECK_VIEW_EVENT, onToggleView);
    return () => window.removeEventListener(TOGGLE_NECK_VIEW_EVENT, onToggleView);
  }, [lessonMode]);

  // Un pas de lecon fabrique un tableau neuf mais ne touche qu'une a quatre
  // cordes. Decoupe par corde en gardant l'identite de la tranche inchangee :
  // les StringRow non concernees restent sur leur memo.
  // Le ref est ecrit pendant le rendu : acceptable ici parce que la comparaison
  // est par contenu, un rendu abandonne ne peut donner qu'un rendu de corde en
  // trop, jamais une tranche fausse.
  const lessonSlicesRef = useRef(null);
  const lessonNotesByString = useMemo(() => {
    const previous = lessonSlicesRef.current;
    const next = Array.from({ length: guitarTuning.length }, (_, stringIndex) => {
      if (!Array.isArray(lessonNotes)) return null;
      const onString = lessonNotes.filter(note => note && note.stringIndex === stringIndex);
      const slice = onString.length ? onString : EMPTY;
      const before = previous && previous[stringIndex];
      return sameLessonSlice(before, slice) ? before : slice;
    });
    lessonSlicesRef.current = next;
    return next;
  }, [lessonNotes, guitarTuning.length]);

  // Les incrustations ne bougent qu'avec le reglage ou la fenetre de frettes,
  // jamais avec un pas de lecon.
  const fretInlays = useMemo(
    () => (neckInlays ? getFretInlays(startFret, numFretsToShow, guitarTuning.length) : NO_INLAYS),
    [neckInlays, startFret, numFretsToShow, guitarTuning.length]
  );
  const openStringMarkerProps = useMemo(() => ({
    lessonMode,
    activeScale,
    selectedNotes,
    highlightArpeggios,
    selectedArpeggio,
    getArpeggioDegree,
    isPaintMode,
    highlightChordNotes,
    getChordDegree,
    currentColor,
    visibleColors,
    handleNoteInteraction,
    noteDisplayMode,
    showWholeScale,
    coachPulse: coachStep === 'walk'
  }), [lessonMode, activeScale, selectedNotes, highlightArpeggios, selectedArpeggio, getArpeggioDegree, isPaintMode, highlightChordNotes, getChordDegree, currentColor, visibleColors, handleNoteInteraction, noteDisplayMode, showWholeScale, coachStep]);


  // Add fretboard logging event listener for keyboard shortcut
  useEffect(() => {
    const handleFretboardLogging = (event) => {
      
      // Collect all the props and state needed for fretboard logging
      const guitarNeckProps = {
        activeScale,
        selectedNotes,
        highlightArpeggios,
        selectedArpeggio,
        highlightChordNotes,
        visibleStrings,
        startFret,
        numFretsToShow,
        currentColor,
        visibleColors,
        highlightedFrets,
        isNoteInPattern: (stringIndex, fret) => isNoteInPattern(stringIndex, fret),
        isArpeggioNote: (stringIndex, fret, activeScale, selectedArpeggio) => 
          isArpeggioNote(stringIndex, fret, activeScale, selectedArpeggio),
        isChordNote: (stringIndex, fret, activeScale) => 
          isChordNote(stringIndex, fret, activeScale),
        getArpeggioDegree: (stringIndex, fret, activeScale, selectedArpeggio) =>
          getArpeggioDegree(stringIndex, fret, activeScale, selectedArpeggio),
        getChordDegree: (stringIndex, fret, activeScale) =>
          getChordDegree(stringIndex, fret, activeScale)
      };
      
      const calculatedState = {
        currentProgression,
        rootNote,
        currentMode,
        activeChordNote,
        displayedChordNote: activeChordNote  // Use activeChordNote as displayedChordNote for now
      };
      
      try {
        // Collect fretboard state
        const fretboardData = collectFretboardState(guitarNeckProps, calculatedState);
        
        // Log the fretboard state
        logService.logFretboardState(fretboardData);
      } catch (error) {
        console.error('[GuitarNeckVisualizer] Error during fretboard logging:', error);
      }
    };
    
    // Listen for the custom fretboard logging event
    window.addEventListener('triggerFretboardLogging', handleFretboardLogging);
    
    // Cleanup
    return () => {
      window.removeEventListener('triggerFretboardLogging', handleFretboardLogging);
    };
  }, [
    activeScale, selectedNotes, highlightArpeggios, selectedArpeggio,
    highlightChordNotes, visibleStrings, startFret, numFretsToShow,
    currentColor, visibleColors, highlightedFrets, currentProgression,
    rootNote, currentMode, isNoteInPattern, isArpeggioNote, isChordNote,
    getArpeggioDegree, getChordDegree
  ]);
  
  // Reset play scroll flag when progression stops
  useEffect(() => {
    if (!isProgressionPlaying) {
      hasScrolledForPlayRef.current = false;
    }
  }, [isProgressionPlaying]);

  // Effect to scroll to the lowest note in the pattern when pattern changes
  useEffect(() => {
    if (scrollToFret != null || isFretboardLocked || !fretsContainerRef.current) return;
    // During progression play: skip if user scrolled manually, or if we already scrolled once
    if (isProgressionPlaying && (hasUserScrolledRef.current || hasScrolledForPlayRef.current)) return;

    const scrollPosition = lowestNoteOnAnyString < 3
      ? 0
      : (Math.max(1, lowestNoteOnAnyString) - 1) * 60;

    scrollFromNut(scrollPosition);

    // Mark that we've done the initial scroll for this play session
    if (isProgressionPlaying) {
      hasScrolledForPlayRef.current = true;
    }
  }, [lowestNoteOnAnyString, pattern, isFretboardLocked, isProgressionPlaying, scrollFromNut, scrollToFret]);

  // Auto-scroll pilote par le lecteur de lecon : la frette demandee vient se
  // placer a une frette et demie du bord gauche. Sur desktop le manche entier
  // tient a l'ecran, il n'y a rien a faire defiler.
  useEffect(() => {
    // Une frette non finie (pas sans note : Math.min d'une liste vide) ne
    // demande rien : scrollTo la prendrait pour le bout du manche ou le sillet.
    if (scrollToFret == null || !Number.isFinite(scrollToFret) || scrollToFret === lastScrollToFretRef.current) return;
    lastScrollToFretRef.current = scrollToFret;
    const element = fretsContainerRef.current;
    if (isFretboardLocked || !element) return;
    // Meme respect du scroll manuel que les patterns 3nps, mais sans le verrou
    // "une seule fois par lecture" : le pas a pas doit suivre chaque shape.
    if (isProgressionPlaying && hasUserScrolledRef.current) return;

    const FRET_WIDTH = 60;
    // Un pas deja sous les yeux ne fait pas bouger le manche. Sans ce test, une
    // lecon qui reste dans la meme position fait glisser le manche a chaque pas,
    // d'une frette ou deux, pendant toute la lecture.
    // Les cordes a vide vivent dans la colonne des noms de corde, qui ne defile
    // jamais : elles sont toujours sous les yeux et ne decident de rien.
    const stepFrets = (Array.isArray(lessonNotes) ? lessonNotes : [])
      .filter((note) => note && note.current && Number.isFinite(note.fret) && note.fret > 0)
      .map((note) => note.fret);
    const frets = stepFrets.length ? stepFrets : (scrollToFret > 0 ? [scrollToFret] : []);
    if (!frets.length) return;
    if (element.clientWidth) {
      const distance = handednessRef.current
        ? Math.max(0, element.scrollWidth - element.clientWidth - element.scrollLeft)
        : element.scrollLeft;
      const firstVisibleFret = distance / FRET_WIDTH + 1;
      const lastVisibleFret = (distance + element.clientWidth) / FRET_WIDTH;
      const margin = 0.5;
      if (Math.min(...frets) >= firstVisibleFret - margin && Math.max(...frets) <= lastVisibleFret + margin) return;
    }

    scrollFromNut(Math.max(0, (scrollToFret - 1.5) * FRET_WIDTH));
  }, [scrollToFret, lessonNotes, isFretboardLocked, isProgressionPlaying, scrollFromNut]);

  // Appareil jugé léger, ou variante B/D du point 5 pendant une leçon : le manche
  // perd animations et transitions, dimensions et couleurs inchangées.
  const reducedMotion = isLightDevice() || (noLessonAnimations() && lessonMode);

  return (
    <Box className={`guitar-neck-visualizer${reducedMotion ? ' neck-reduced-motion' : ''}${coachStep === 'droneTip' ? ' coach-root-pulse' : ''} ${className}`} sx={{
      width: '100%',
      height: 'auto',
      position: 'relative',
      zIndex: 'auto',
      backgroundColor: 'transparent',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'visible',
      padding: 0
    }}>
      <NeckControls
        activeScale={activeScale}
        startFret={startFret}
        startPatternFret={pattern[0] && pattern[0].length > 0 ? pattern[0][0] : null}
        highlightArpeggios={highlightArpeggios}
        setHighlightArpeggios={setHighlightArpeggios}
        showConnectionLines={showConnectionLines}
        setShowConnectionLines={setShowConnectionLines}
        showWholeScale={showWholeScale}
        setShowWholeScale={setShowWholeScale}
        lessonMode={lessonNotes !== null}
        trackedDegree={trackedDegree}
        setTrackedDegree={setTrackedDegree}
        chordExtension={chordExtension}
        selectedNotes={selectedNotes}
        isStartNoteSelectionMode={isStartNoteSelectionMode}
        onStartNoteSelectionMode={setIsStartNoteSelectionMode}
        rootNote={rootNote}
        currentMode={currentMode}
        activeChordNote={activeChordNote}
        playingNote={playingNote}
        highlightChordNotes={highlightChordNotes}
        setHighlightChordNotes={setHighlightChordNotes}
        onClearSelectedNotes={() => {
          if (onSelectedNotesChange) {
            onSelectedNotesChange([]);
            setHasSelectedNote(false);
          }
          // Clear any highlighted frets
          handleResetFretSelection();
          // Reset arpeggio highlighting
          setHighlightArpeggios(false);
          setSelectedArpeggio(null);
          // Auto-close paint mode
          if (onPaintModeChange) {
            onPaintModeChange(false);
          }
        }}
        selectedArpeggio={selectedArpeggio}
        setSelectedArpeggio={setSelectedArpeggio}
        currentColor={currentColor}
        onColorSelect={(color) => setCurrentColor(color)}
        visibleColors={visibleColors}
        onToggleColorVisibility={handleToggleColorVisibility}
        isFretboardLocked={isFretboardLocked}
        onToggleFretboardLock={handleToggleFretboardLock}
        onShowPatternSnackbar={onShowPatternSnackbar}
        currentProgression={currentProgression}
        isPaintMode={isPaintMode}
        onPaintModeChange={onPaintModeChange}
        noteDisplayMode={noteDisplayMode}
        setNoteDisplayMode={setNoteDisplayMode}
        className="neck-controls"
        isCompactMode={isCompactMode}
        earSequence={earSequence}
        onEarStep={setEarStepIndex}
      />
      
      <Box sx={{ 
        width: '100%',
        flex: 'none',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Fretboard Container - Combines headers and strings into one scrollable unit */}
          <Box className="fretboard-wrapper" sx={{
            flexDirection: leftHanded ? 'row-reverse' : 'row',
            display: 'flex',
            position: 'relative',
            '@media (orientation: landscape)': {
              [leftHanded ? 'paddingRight' : 'paddingLeft']: '10px'
            }
          }}>
          {/* Floating Lock Button Overlay */}
          <FretboardLockOverlay
            isLocked={isFretboardLocked}
            leftHanded={leftHanded}
            onToggle={handleToggleFretboardLock}
          />
          {/* Tips de decouverte : deux gestes que rien n'indique a l'ecran. Le
              wrapper de taille nulle sert d'ancre, la bulle se pose au-dessus du
              manche et pointe vers le bas, sur la colonne des cordes ou sur la
              bande des numeros de frettes. */}
          {discoveryTip === 'strings' && (
            <Box sx={{ position: 'absolute', top: 0, [leftHanded ? 'right' : 'left']: 0, width: 0, height: 0 }}>
              <Tip
                text="Hold a string name to focus on it."
                align={leftHanded ? 'end' : 'start'}
                duration={8000}
                onDismiss={onDiscoveryTipDismiss}
              />
            </Box>
          )}
          {discoveryTip === 'frets' && (
            <Box sx={{ position: 'absolute', top: 0, left: '50%', width: 0, height: 0 }}>
              <Tip
                text="Tap a fret number to focus on that position."
                duration={8000}
                onDismiss={onDiscoveryTipDismiss}
              />
            </Box>
          )}
          {/* Fixed string names column */}
          <Box 
            className="fretboard-string-names-column"
            sx={{
              width: '30px',
              '@media (orientation: landscape)': {
                width: '24px',
              },
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
            }}
          >
            {/* Top header placeholder */}
            <Box 
              className="fretboard-string-column-placeholder top"
              sx={{ 
                height: '36px', // Match the height of the header
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
              }}
            ></Box>
            
            {/* String name labels */}
            {[...guitarTuning].reverse().map((string, stringIndex) => {
              const actualStringIndex = guitarTuning.length - 1 - stringIndex;
              // Meme tranche que la corde : la case frette 0 et le reste de la
              // corde lisent la meme source, meme niveau 2 ou 3 garanti.
              const slice = lessonNotesByString[actualStringIndex];
              const lessonNote = slice ? (slice.find(n => n.fret === 0) || null) : null;
              return (
                <StringLabelCell
                  key={`string-name-${actualStringIndex}`}
                  leftHanded={leftHanded}
                  stringName={string}
                  stringIndex={actualStringIndex}
                  cell={openStringCells[actualStringIndex]}
                  lessonNote={lessonNote}
                  noteOpacity={visibleStrings === null || visibleStrings.has(actualStringIndex) ? 1 : 0.1}
                  isEarLit={!!earPosition && earPosition.stringIndex === actualStringIndex && earPosition.fret === 0}
                  markerProps={openStringMarkerProps}
                  onTap={() => playOpenString(actualStringIndex)}
                  onLongPress={() => toggleStringVisibility(actualStringIndex)}
                />
              );
            })}
            
            {/* Bottom header placeholder. Il devient le bouton qui remet le
                manche a plat des qu'une corde est masquee ou une frette
                surlignee : sinon il faut autant d'appuis longs qu'il y a de
                cordes cachees. Il n'existe pas quand il n'y a rien a annuler,
                donc il ne peut pas etre tape par erreur, et sa seule presence
                dit qu'une corde est cachee. Perimetre strict : ni tonalite, ni
                gamme, ni accord, ni Safe/All, ni position, ni cadenas. */}
            <Box
              className={`fretboard-string-column-placeholder bottom${canResetNeck ? ' resettable' : ''}`}
              component={canResetNeck ? 'button' : 'div'}
              onClick={canResetNeck ? handleResetNeckView : undefined}
              aria-label={canResetNeck ? 'Show all strings and frets again' : undefined}
              sx={{
                height: '36px', // Match the height of the header
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                ...(canResetNeck && {
                  border: 'none',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'rgba(255, 255, 255, 0.75)',
                  // La colonne ne fait que 30px : l'icone deborde sa case pour
                  // rester lisible, donc elle doit passer devant le manche et
                  // non dessous.
                  position: 'relative',
                  overflow: 'visible',
                  zIndex: 6,
                  // La feuille globale impose font-size: 1.4em !important a
                  // toutes les icones MUI : un fontSize dans le sx de l'icone
                  // ne passe pas, il faut la viser depuis le parent avec la
                  // meme arme. Le scale(1.2) global s'y ajoute, le rendu fait
                  // donc environ 43px.
                  '& .MuiSvgIcon-root': { fontSize: '36px !important' },
                  // L'icone depasse sa case : la cible suit, sinon on tape a
                  // cote de ce qu'on voit. En ::after, la mise en page de la
                  // colonne ne bouge pas.
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: 44,
                    height: 44,
                    transform: 'translate(-50%, -50%)'
                  },
                  '&:active': { color: '#fff' }
                })
              }}
            >
              {canResetNeck && (
                <RestartAltIcon
                  sx={{
                    position: 'relative',
                    // Decalee vers le manche, pas centree sous la colonne des
                    // noms : elle y chevauchait le label de corde du dessus.
                    // Cote inverse en mode gaucher, le manche change de bord.
                    left: leftHanded ? '-10px' : '10px',
                    zIndex: 7,
                    // Ombre portee sur le trace de l'icone, pas sur sa boite :
                    // un rectangle d'ombre se verrait sur les cordes derriere.
                    filter: 'drop-shadow(0 1px 3px rgba(0, 0, 0, 0.9))'
                  }}
                />
              )}
            </Box>
          </Box>
          
          {/* Scrollable section containing headers and strings */}
          <Box 
            ref={fretsContainerRef}
            className="fretboard-scrollable-area"
            sx={{
              minWidth: 0,
              flex: 1,
              overflowX: 'auto',
              scrollbarWidth: 'none', // Hide scrollbar for Firefox
              '&::-webkit-scrollbar': { display: 'none' }, // Hide scrollbar for Chrome
              msOverflowStyle: 'none', // Hide scrollbar for IE/Edge
              scrollSnapType: 'x mandatory'
            }}
          >
            <Box 
              className="fretboard-content"
              sx={{ 
                minWidth: `${(numFretsToShow + 1) * 60}px`, 
                display: 'flex', 
                flexDirection: 'column'
              }}
            >
              {/* Top Fretboard Header */}
              <Box 
                ref={topHeaderRef}
                className="fretboard-header-container top"
              >
                <FretboardHeader
                  leftHanded={leftHanded}
                  startFret={startFret} 
                  numFretsToShow={numFretsToShow} 
                  dotsPosition="bottom" 
                  hideStringColumn={true}
                  className="top-fretboard"
                  onFretClick={handleFretClick}
                  highlightedFrets={highlightedFrets}
                />
              </Box>
              
              {/* Guitar Strings */}
              <Box 
                ref={fretboardRef}
                className="fretboard-strings-container"
                sx={{
                  position: 'relative',
                  width: '100%',
                  minWidth: `${(numFretsToShow + 1) * 60}px` // Match parent's minWidth
                }}
              >
                {/* Sillet : l'os de la frette 0. Seulement quand le manche part
                    vraiment du debut, sinon la barre mentirait sur la position.
                    Masque pour le moment (SHOW_NUT). */}
                {SHOW_NUT && startFret === 1 && (
                  <Box
                    className="fretboard-nut"
                    aria-hidden="true"
                    sx={{
                      position: 'absolute',
                      [leftHanded ? 'right' : 'left']: 0,
                      top: 0,
                      bottom: 0,
                      width: '4px',
                      background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.34) 0%, rgba(255, 255, 255, 0.16) 100%)',
                      pointerEvents: 'none',
                      zIndex: 2
                    }}
                  />
                )}
                {/* Incrustations : repere peripherique, sous les cordes et les notes */}
                {fretInlays.map(({ fret, fretIndex, boundary }) => {
                  const isDouble = DOUBLE_DOT_FRETS.includes(fret);
                  const size = isDouble ? 0.2 : 0.17;
                  return (
                    <Box
                      key={`inlay-${fret}-${boundary}`}
                      className={`fretboard-inlay${isDouble ? ' double' : ''}`}
                      data-fret={fret}
                      sx={{
                        position: 'absolute',
                        [leftHanded ? 'right' : 'left']: `${(fretIndex + 0.5) * 60}px`,
                        top: `calc(${boundary} * var(--string-row-height, 36px))`,
                        width: `calc(var(--string-row-height, 36px) * ${size})`,
                        height: `calc(var(--string-row-height, 36px) * ${size})`,
                        transform: `translate(${leftHanded ? '50%' : '-50%'}, -50%)`,
                        borderRadius: '50%',
                        backgroundColor: isDouble ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.85)',
                        boxShadow: isDouble
                          ? '0 0 0 3px rgba(0, 0, 0, 0.22), 0 1px 2px rgba(0, 0, 0, 0.35)'
                          : '0 0 0 2px rgba(0, 0, 0, 0.18)',
                        pointerEvents: 'none',
                        zIndex: 1
                      }}
                    />
                  );
                })}
                {[...guitarTuning].reverse().map((string, stringIndex) => {
                  // Need to get the actual string index in the original array
                  const actualStringIndex = guitarTuning.length - 1 - stringIndex;
                  
                  return (
                    <StringRow
                      leftHanded={leftHanded}
                      key={actualStringIndex}
                      stringName={string}
                      stringIndex={actualStringIndex}
                      startFret={startFret}
                      numFretsToShow={numFretsToShow}
                      activeScale={activeScale}
                      pattern={pattern}
                      selectedNotes={selectedNotes}
                      hasSelectedNote={hasSelectedNote}
                      highlightArpeggios={highlightArpeggios}
                      isNoteInPattern={effectiveIsNoteInPattern}
                      showWholeScale={showWholeScale}
                      isArpeggioNote={isArpeggioNote}
                      getArpeggioDegree={getArpeggioDegree}
                      highlightChordNotes={highlightChordNotes}
                      isChordNote={isChordNote}
                      getChordDegree={getChordDegree}
                      visibleStrings={visibleStrings}
                      toggleStringVisibility={toggleStringVisibility}
                      handleNoteClick={handleNoteClick}
                      handleNoteInteraction={handleNoteInteraction}
                      isPaintMode={isPaintMode}
                      drawConnections={showConnectionLines}
                      hideStringColumn={true}
                      className={`string-row`}
                      highlightedFrets={highlightedFrets}
                      currentColor={currentColor}
                      visibleColors={visibleColors}
                      selectedArpeggio={selectedArpeggio}
                      noteDisplayMode={noteDisplayMode}
                      fretMarkerDots={fretMarkerDots}
                      earPosition={earPosition}
                      coachPulse={coachStep === 'walk'}
                      lessonNotes={lessonNotesByString[actualStringIndex]}
                    />
                  );
                })}
              </Box>
              
              {/* Bottom Fretboard Header */}
              <Box 
                ref={bottomHeaderRef}
                className="fretboard-header-container bottom"
              >
                <FretboardHeader
                  leftHanded={leftHanded}
                  startFret={startFret} 
                  numFretsToShow={numFretsToShow} 
                  dotsPosition="top" 
                  hideStringColumn={true}
                  className="bottom-fretboard"
                  onFretClick={handleFretClick}
                  highlightedFrets={highlightedFrets}
                />
              </Box>
            </Box>
          </Box>
        </Box>
        

        
        {/* Highlight controls removed */}
        
        {/* Preset dialogs */}
        <PresetDialogs
          presetDialogOpen={presetDialogOpen}
          setPresetDialogOpen={setPresetDialogOpen}
          presetName={presetName}
          setPresetName={setPresetName}
          loadPresetDialogOpen={loadPresetDialogOpen}
          setLoadPresetDialogOpen={setLoadPresetDialogOpen}
          savePreset={savePreset}
          loadPreset={loadPreset}
          deletePreset={deletePreset}
          presets={presets}
          updateFunctions={{
            setSelectedNotes: onSelectedNotesChange, // Use the callback from props
            setHasSelectedNote,
            setVisibleStrings
          }}
          className="preset-dialogs"
        />
      </Box>
    </Box>
  );
};

export default React.memo(GuitarNeckVisualizer);
